import { Bike, Link2 } from 'lucide-react';
import { useState } from 'react';
import type { Threshold } from '@painpal/contracts';
import { useThresholds, useThresholdSuggestions } from '../../api/queries';
import { formatDayMonthYear } from '../../lib/format';
import { SportSuggestion } from './SportSuggestion';
import { StepChart } from './StepChart';
import { ThresholdHistory, type Editing } from './ThresholdHistory';
import {
  currentThreshold, formatThresholdValue, paceStripLabels, paceZoneBounds, SPORT_LTHR_COPY, THRESHOLD_COPY, thresholdSegments,
} from './thresholdModel';
import { ZoneStrip } from './ZoneStrip';
import settings from './settings.module.css';
import styles from './sportSettings.module.css';

const PACE = THRESHOLD_COPY.threshold_pace;
const HR = SPORT_LTHR_COPY.run;

function MaxHrShared({ today, onEditMaxHr }: { today: string; onEditMaxHr: () => void }) {
  const current = currentThreshold(useThresholds('max_hr').data?.items ?? [], today);
  if (current === undefined) return null;
  return (
    <div className={styles.maxHrShared}>
      <Link2 size={14} aria-hidden="true" />
      <div>
        <p className={styles.maxHrLine}>{`MAX HR ${current.value} BPM · SHARED BY EVERY SPORT`}</p>
        <p className={settings.note}>One value for you, edited under RIDE. Run HR zones use the threshold above.</p>
        <button type="button" className={`btn btn-outline ${styles.editUnderRide}`} onClick={onEditMaxHr}><Bike size={14} aria-hidden="true" />EDIT UNDER RIDE ›</button>
      </div>
    </div>
  );
}

function PaceCard({ today }: { today: string }) {
  const [editing, setEditing] = useState<Editing>(null);
  const runPace = useThresholdSuggestions().data?.runPace;
  return (
    <section className={settings.ftpPanel} aria-labelledby="run-pace-title">
      <header className={settings.ftpHead}>
        <div>
          <h2 id="run-pace-title" className={settings.ftpTitle}>THRESHOLD PACE HISTORY</h2>
          <p className={settings.ftpSub}>EACH RUN USES THE THRESHOLD PACE THAT WAS VALID ON ITS DATE · MIN/KM</p>
        </div>
        <button type="button" className={`btn btn-pink ${settings.addFtp}`} onClick={() => setEditing({ mode: 'new' })}>+ ADD PACE</button>
      </header>
      <SportSuggestion action="run-pace" suggestion={runPace} />
      <ThresholdHistory
        copy={PACE} today={today} editing={editing} setEditing={setEditing}
        summary={(items) => items.length > 0 && <StepChart segments={thresholdSegments(items, today, PACE)} copy={PACE} />}
      />
      <PaceZones today={today} />
      <p className={`sr-only-phone ${styles.cardNote}`}>Runs with GPS or a pace stream use grade-adjusted pace against this. Treadmill runs with no pace fall back to run threshold HR.</p>
    </section>
  );
}

function PaceZones({ today }: { today: string }) {
  const current = currentThreshold(useThresholds('threshold_pace', 'run').data?.items ?? [], today);
  if (current === undefined) return null;
  return <ZoneStrip model="pace" title={`YOUR PACE ZONES FROM ${formatThresholdValue(PACE, current.value)}`} labels={paceStripLabels(paceZoneBounds(current.value))} />;
}

function RunHrCard({ today, onEditMaxHr }: { today: string; onEditMaxHr: () => void }) {
  const [editing, setEditing] = useState<Editing>(null);
  const runLthr = useThresholdSuggestions().data?.runLthr;
  return (
    <section className={`${settings.card} ${styles.hrCard}`} data-tone="strong" aria-labelledby="run-hr-title">
      <h2 id="run-hr-title" className={settings.cardTitle}>RUN THRESHOLD HR</h2>
      <div className={settings.hrHistory}>
        <ThresholdHistory
          copy={HR} today={today} editing={editing} setEditing={setEditing}
          summary={(items) => (
            <>
              {items.length > 0 && <CurrentHr items={items} today={today} onAdd={() => setEditing({ mode: 'new' })} />}
              <SportSuggestion action="run-lthr" suggestion={runLthr} compact />
            </>
          )}
        />
      </div>
      <MaxHrShared today={today} onEditMaxHr={onEditMaxHr} />
      <p className={`sr-only-phone ${settings.note}`}>Also used for treadmill runs with no pace stream: their Load comes from HR against this value.</p>
    </section>
  );
}

function CurrentHr({ items, today, onAdd }: { items: Threshold[]; today: string; onAdd: () => void }) {
  const current = currentThreshold(items, today);
  return (
    <div className={settings.hrCurrent}>
      {current !== undefined && (
        <p className={settings.hrValueLine}>
          <span className={settings.hrValue}>{`${current.value} BPM`}</span>
          <span className={settings.hrSince}>{`THRESHOLD HR · SINCE ${formatDayMonthYear(current.effectiveFrom)}`}</span>
        </p>
      )}
      <button type="button" className="btn btn-outline" onClick={onAdd}>+ ADD HR</button>
    </div>
  );
}

export function RunThresholds({ today, onEditMaxHr }: { today: string; onEditMaxHr: () => void }) {
  return (
    <>
      <div className={settings.thresholds}><PaceCard today={today} /></div>
      <div className={settings.sideTop}><RunHrCard today={today} onEditMaxHr={onEditMaxHr} /></div>
    </>
  );
}
